/**
 * CrisisAlert — Sicherheits-Layer für AYU-Chats
 * Erkennt Krisen-Keywords und zeigt Notfall-Box mit Kontakten.
 */
import { useState } from "react";
import { Phone, MessageCircle, Heart, X, AlertTriangle } from "lucide-react";

const CRISIS_KEYWORDS = [
  "suizid", "umbringen", "nicht mehr leben", "selbstmord",
  "selbstverletzung", "ritzen", "aufhören zu leben",
  "alles beenden", "keinen sinn mehr", "will sterben",
  "will nicht mehr", "töten", "aufgeben",
  "es lohnt sich nicht mehr", "nicht mehr aufwachen",
  "wäre besser ohne mich", "niemandem fehlen",
  "keine hoffnung", "alles sinnlos",
];

export function containsCrisisKeywords(text: string): boolean {
  const lower = text.toLowerCase();
  return CRISIS_KEYWORDS.some((keyword) => lower.includes(keyword));
}

interface CrisisAlertProps {
  visible: boolean;
  onDismiss: () => void;
  accentColor?: string;
}

export default function CrisisAlert({ visible, onDismiss, accentColor = "#6b7f4e" }: CrisisAlertProps) {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="mx-4 w-full max-w-md bg-white rounded-2xl shadow-2xl border border-red-900/30 overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-900/30 to-orange-900/20 px-6 py-4 border-b border-red-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-semibold text-[#2d3a1e] text-base">
                  Du bist nicht allein
                </h3>
                <p className="text-xs text-[#5a6b4a]">
                  Hilfe ist nur einen Anruf entfernt
                </p>
              </div>
            </div>
            <button
              onClick={onDismiss}
              className="text-[#5a6b4a] hover:text-[#2d3a1e] transition-colors p-1"
              title="Schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#4a5e38] leading-relaxed">
            Was du gerade fühlst, ist real und wichtig. Bitte sprich jetzt mit
            jemandem, der dir sofort helfen kann:
          </p>

          <div className="space-y-3">
            <a
              href="tel:08001110111"
              className="flex items-center gap-4 p-4 rounded-xl bg-red-900/20 border border-red-900/30 hover:bg-red-900/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-[#2d3a1e] text-sm">Telefonseelsorge</p>
                <p className="text-lg font-bold text-red-400">0800 111 0 111</p>
                <p className="text-xs text-[#5a6b4a]">Kostenlos, 24/7, anonym</p>
              </div>
            </a>

            <a
              href="tel:08001110222"
              className="flex items-center gap-4 p-4 rounded-xl bg-orange-900/20 border border-orange-900/30 hover:bg-orange-900/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-full bg-orange-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-[#2d3a1e] text-sm">Telefonseelsorge (Alternativ)</p>
                <p className="text-lg font-bold text-orange-400">0800 111 0 222</p>
                <p className="text-xs text-[#5a6b4a]">Kostenlos, 24/7, anonym</p>
              </div>
            </a>

            <a
              href="https://online.telefonseelsorge.de"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-xl bg-blue-900/20 border border-blue-900/30 hover:bg-blue-900/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-[#2d3a1e] text-sm">Online-Chat</p>
                <p className="text-sm font-medium text-blue-400">online.telefonseelsorge.de</p>
                <p className="text-xs text-[#5a6b4a]">Schriftlich, kostenlos, anonym</p>
              </div>
            </a>
          </div>

          <div className="flex items-start gap-3 bg-[#f0f3eb] rounded-xl p-4">
            <Heart className="w-5 h-5 shrink-0 mt-0.5" style={{ color: accentColor }} />
            <p className="text-xs text-[#4a5e38] leading-relaxed">
              Diese Gefühle können sich ändern, und es gibt Menschen, die genau
              dafür ausgebildet sind, dir jetzt zu helfen. Du verdienst
              Unterstützung. AYU ist danach weiterhin für dich da.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#d4dcc4] bg-[#f0f3eb]/50">
          <button
            onClick={onDismiss}
            className="w-full text-center text-sm py-2.5 rounded-xl transition-colors hover:bg-[#6b7f4e]/10 text-[#5a6b4a]"
          >
            Ich möchte weiterschreiben
          </button>
        </div>
      </div>
    </div>
  );
}
